/**
 * Construit le SVG imbriqué représentant un logo recadré/zoomé (mêmes
 * paramètres que LogoPreview), en pur SVG vectoriel destiné à svg2pdf.js —
 * voir ARCHITECTURE.md pour pourquoi (rendu vectoriel net à toute échelle,
 * plutôt qu'un raster). Le recadrage carré est obtenu en donnant à
 * l'élément externe son propre `viewBox="0 0 size size"` (fenêtre de
 * découpe) et en imbriquant dedans le SVG d'origine, positionné/dimensionné
 * pour reproduire le même calcul d'échelle+centrage+décalage que
 * LogoPreview (voir sa docstring).
 */
function parseViewBox(root: Element): { minX: number; minY: number; width: number; height: number } | null {
  const vb = root.getAttribute('viewBox');
  if (!vb) return null;
  const parts = vb.trim().split(/[\s,]+/).map(Number);
  if (parts.length !== 4 || parts.some((n) => Number.isNaN(n))) return null;
  const [minX, minY, width, height] = parts;
  if (width <= 0 || height <= 0) return null;
  return { minX, minY, width, height };
}

/**
 * Les logos peuvent être insérés plusieurs fois sur une même page (un par
 * page répétée) et sur la même page que d'autres logos : un `id` défini par
 * un logo (dégradé, clipPath...) pourrait sinon entrer en collision avec le
 * même `id` défini par un autre logo dans le même document PDF/SVG le temps
 * de la conversion. On préfixe donc tous les `id` et leurs références
 * (`url(#id)`, `href="#id"`) par un préfixe unique à cet appel.
 */
function namespaceIds(markup: string, prefix: string): string {
  const ids = new Set<string>();
  const idRe = /\bid="([^"]+)"/g;
  let m: RegExpExecArray | null;
  while ((m = idRe.exec(markup))) ids.add(m[1]);
  if (ids.size === 0) return markup;
  let out = markup;
  for (const id of ids) {
    const escaped = id.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    out = out
      .replace(new RegExp(`id="${escaped}"`, 'g'), `id="${prefix}${id}"`)
      .replace(new RegExp(`url\\(#${escaped}\\)`, 'g'), `url(#${prefix}${id})`)
      .replace(new RegExp(`(xlink:href|href)="#${escaped}"`, 'g'), `$1="#${prefix}${id}"`);
  }
  return out;
}

/** Retourne le markup SVG imbriqué prêt à insérer dans une page, ou null si le contenu n'est pas un SVG exploitable. */
export function buildLogoSvgMarkup(svgContent: string, cropX: number, cropY: number, zoom: number, size: number, uniquePrefix: string): string | null {
  let root: Element;
  try {
    const doc = new DOMParser().parseFromString(svgContent, 'image/svg+xml');
    if (doc.querySelector('parsererror')) return null;
    root = doc.documentElement;
    if (root.tagName.toLowerCase() !== 'svg') return null;
  } catch {
    return null;
  }

  const viewBox = parseViewBox(root);
  const attrW = parseFloat(root.getAttribute('width') || '');
  const attrH = parseFloat(root.getAttribute('height') || '');
  const naturalW = viewBox?.width || (Number.isFinite(attrW) && attrW > 0 ? attrW : 100);
  const naturalH = viewBox?.height || (Number.isFinite(attrH) && attrH > 0 ? attrH : 100);

  const baseScale = Math.min(size / naturalW, size / naturalH);
  const scale = baseScale * zoom;
  const w = naturalW * scale;
  const h = naturalH * scale;
  const left = (size - w) / 2 - cropX * size;
  const top = (size - h) / 2 - cropY * size;

  const innerViewBox = viewBox ? `${viewBox.minX} ${viewBox.minY} ${viewBox.width} ${viewBox.height}` : `0 0 ${naturalW} ${naturalH}`;
  const innerMarkup = namespaceIds(root.innerHTML, uniquePrefix);

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}">` +
    `<svg x="${left}" y="${top}" width="${w}" height="${h}" viewBox="${innerViewBox}">${innerMarkup}</svg>` +
    `</svg>`
  );
}
